/**
 * CR168: Flyer Input Mode Selector for Batch Run Wizard
 * CR419: Show expired flyers toggle for testing
 * Toggle between Bucket (auto-discover) and Upload modes
 */

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  FolderOpen,
  Upload,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  HelpCircle,
  Info,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useFlyerBucketSources } from "@/hooks/useFlyerBucketSources";
import { useFlyerBucketDiscovery } from "@/hooks/useFlyerBucketDiscovery";
import type { FlyerInputMode, DiscoveredFlyer } from "@/lib/flyer-bucket/types";
import { AddAliasFromDiscoveryDialog } from "./AddAliasFromDiscoveryDialog";

interface FlyerInputModeSelectorProps {
  mode: FlyerInputMode;
  onModeChange: (mode: FlyerInputMode) => void;
  bucketSourceId: string | null;
  onBucketSourceChange: (id: string | null) => void;
  selectedFlyers: DiscoveredFlyer[];
  onSelectedFlyersChange: (flyers: DiscoveredFlyer[]) => void;
  children?: React.ReactNode; // For Upload mode content
}

/** CR419: Flyer row with status chip */
function FlyerRow({
  flyer,
  isSelected,
  onToggle,
}: {
  flyer: DiscoveredFlyer;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
      <Checkbox checked={isSelected} onCheckedChange={onToggle} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-2">
          {flyer.filename}
          {flyer.is_expired ? (
            <Badge variant="warning" className="text-xs h-4">Expired</Badge>
          ) : (
            <Badge variant="outline" className="text-xs h-4">Active</Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <span>{flyer.matched_merchant_id}</span>
          <Badge variant="outline" className="text-xs h-4">
            {flyer.flyer_type_normalized}
          </Badge>
          <span>{flyer.start_date} → {flyer.end_date}</span>
        </div>
      </div>
    </div>
  );
}

export function FlyerInputModeSelector({
  mode,
  onModeChange,
  bucketSourceId,
  onBucketSourceChange,
  selectedFlyers,
  onSelectedFlyersChange,
  children,
}: FlyerInputModeSelectorProps) {
  const { sources, loading: loadingSources } = useFlyerBucketSources();
  const { discovering, result, runDiscovery, clearResult } = useFlyerBucketDiscovery();

  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasFlyer, setAliasFlyer] = useState<DiscoveredFlyer | null>(null);

  // CR419: Expired flyers toggle state
  const [showExpired, setShowExpired] = useState(false);
  const [expiredSectionOpen, setExpiredSectionOpen] = useState(false);

  // CR419: Count of selected expired flyers (even when toggle is OFF)
  const selectedExpiredCount = useMemo(() => {
    if (!result) return 0;
    return result.expired_flyers.filter((f) => selectedPaths.has(f.object_path)).length;
  }, [selectedPaths, result]);

  // Auto-run discovery when bucket source changes
  useEffect(() => {
    if (mode === "bucket" && bucketSourceId) {
      runDiscovery(bucketSourceId);
    } else {
      clearResult();
    }
  }, [mode, bucketSourceId]);

  // Sync selected paths with parent (include both eligible + expired selections)
  useEffect(() => {
    if (result) {
      const allFlyers = [...result.eligible_flyers, ...result.expired_flyers];
      const selected = allFlyers.filter((f) => selectedPaths.has(f.object_path));
      onSelectedFlyersChange(selected);
    }
  }, [selectedPaths, result]);

  // Auto-select all eligible on new discovery (not expired)
  useEffect(() => {
    if (result) {
      setSelectedPaths(new Set(result.eligible_flyers.map((f) => f.object_path)));
    }
  }, [result]);

  const handleModeChange = (newMode: FlyerInputMode) => {
    onModeChange(newMode);
    if (newMode === "upload") {
      clearResult();
      onSelectedFlyersChange([]);
    }
  };

  const handleToggleSelect = (path: string) => {
    const newSelected = new Set(selectedPaths);
    if (newSelected.has(path)) {
      newSelected.delete(path);
    } else {
      newSelected.add(path);
    }
    setSelectedPaths(newSelected);
  };

  const handleSelectAll = () => {
    if (result) {
      setSelectedPaths(new Set(result.eligible_flyers.map((f) => f.object_path)));
    }
  };

  const handleClearSelection = () => {
    setSelectedPaths(new Set());
  };

  const handleAddAlias = (flyer: DiscoveredFlyer) => {
    setAliasFlyer(flyer);
    setAliasDialogOpen(true);
  };

  const handleAliasSuccess = () => {
    if (bucketSourceId) {
      runDiscovery(bucketSourceId);
    }
  };

  const enabledSources = sources.filter((s) => s.is_enabled);

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="space-y-2">
        <Label className="text-base font-medium">Flyer Input Mode</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "bucket" ? "default" : "outline"}
            className="flex-1"
            onClick={() => handleModeChange("bucket")}
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Bucket (auto-discover)
          </Button>
          <Button
            type="button"
            variant={mode === "upload" ? "default" : "outline"}
            className="flex-1"
            onClick={() => handleModeChange("upload")}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload PDFs
          </Button>
        </div>
      </div>

      {/* Bucket Mode UI */}
      {mode === "bucket" && (
        <div className="space-y-4">
          {/* Source Selector */}
          <div className="space-y-2">
            <Label>Flyer Bucket Source</Label>
            <Select
              value={bucketSourceId || ""}
              onValueChange={(v) => onBucketSourceChange(v || null)}
              disabled={loadingSources}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a bucket source..." />
              </SelectTrigger>
              <SelectContent>
                {enabledSources.length === 0 ? (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    No enabled bucket sources
                  </div>
                ) : (
                  enabledSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Discovery Status */}
          {discovering && (
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm">Discovering flyers...</span>
            </div>
          )}

          {/* Discovery Results */}
          {result && !discovering && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {result.summary.eligible_count} Eligible
                </Badge>
                {result.summary.expired_count > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <Clock className="h-3 w-3" />
                    {result.summary.expired_count} Expired
                  </Badge>
                )}
                {result.summary.unmatched_merchant_count > 0 && (
                  <Badge variant="outline" className="gap-1">
                    <HelpCircle className="h-3 w-3" />
                    {result.summary.unmatched_merchant_count} Unmatched
                  </Badge>
                )}
                {result.summary.invalid_format_count > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    {result.summary.invalid_format_count} Invalid
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => bucketSourceId && runDiscovery(bucketSourceId)}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>

              {/* CR419: Show Expired Toggle */}
              {result.expired_flyers.length > 0 && (
                <div className="flex items-center gap-3 p-2 rounded-lg border bg-muted/20">
                  <Switch
                    id="show-expired"
                    checked={showExpired}
                    onCheckedChange={setShowExpired}
                  />
                  <Label htmlFor="show-expired" className="text-sm cursor-pointer">
                    Show expired flyers
                  </Label>
                  <span className="text-xs text-muted-foreground italic">For testing only</span>
                  {/* CR419: Persist note when toggle OFF but expired selected */}
                  {!showExpired && selectedExpiredCount > 0 && (
                    <Badge variant="warning" className="text-xs ml-auto gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {selectedExpiredCount} expired selected
                    </Badge>
                  )}
                </div>
              )}

              {/* Eligible Flyers List */}
              {result.eligible_flyers.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">
                      Select Flyers ({selectedPaths.size} of{" "}
                      {result.eligible_flyers.length + (showExpired ? result.expired_flyers.length : 0)})
                    </Label>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearSelection}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[200px] border rounded-md">
                    <div className="p-2 space-y-1">
                      {/* Active flyers */}
                      {result.eligible_flyers.map((flyer) => (
                        <FlyerRow
                          key={flyer.object_path}
                          flyer={flyer}
                          isSelected={selectedPaths.has(flyer.object_path)}
                          onToggle={() => handleToggleSelect(flyer.object_path)}
                        />
                      ))}

                      {/* CR419: Expired flyers (collapsed section) */}
                      {showExpired && result.expired_flyers.length > 0 && (
                        <Collapsible
                          open={expiredSectionOpen}
                          onOpenChange={setExpiredSectionOpen}
                        >
                          <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 rounded hover:bg-muted/50 text-sm font-medium text-muted-foreground">
                            {expiredSectionOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <Clock className="h-3.5 w-3.5" />
                            Expired flyers ({result.expired_flyers.length})
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-1 pl-2 border-l-2 border-warning/30 ml-2">
                            {result.expired_flyers.map((flyer) => (
                              <FlyerRow
                                key={flyer.object_path}
                                flyer={flyer}
                                isSelected={selectedPaths.has(flyer.object_path)}
                                onToggle={() => handleToggleSelect(flyer.object_path)}
                              />
                            ))}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* CR419: Warning callout when expired flyers selected */}
              {selectedExpiredCount > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-warning/50 bg-warning/10 text-sm">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <span>
                    You selected <strong>{selectedExpiredCount}</strong> expired flyer
                    {selectedExpiredCount > 1 ? "s" : ""}. This is intended for testing;
                    extracted deals may be out of date.
                  </span>
                </div>
              )}

              {/* Unmatched Merchants (with add alias flow) */}
              {result.unmatched_merchants.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <Label className="text-sm">
                      Unmatched Merchants ({result.unmatched_merchants.length})
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="text-xs">
                            These flyers couldn't be matched to a merchant.
                            Add an alias to include them.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="border rounded-md divide-y max-h-[120px] overflow-y-auto">
                    {result.unmatched_merchants.map((flyer) => (
                      <div
                        key={flyer.object_path}
                        className="flex items-center justify-between p-2 text-sm"
                      >
                        <span className="truncate flex-1 text-muted-foreground">
                          {flyer.merchant_key_raw}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => handleAddAlias(flyer)}
                        >
                          Add alias
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!bucketSourceId && !discovering && (
            <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/20">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a bucket source to discover flyers</p>
            </div>
          )}
        </div>
      )}

      {/* Upload Mode UI */}
      {mode === "upload" && children}

      <AddAliasFromDiscoveryDialog
        open={aliasDialogOpen}
        onOpenChange={setAliasDialogOpen}
        flyer={aliasFlyer}
        onSuccess={handleAliasSuccess}
      />
    </div>
  );
}
